import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ExternalLink, HelpCircle } from 'lucide-react';
import { ayudaDeRuta } from '../ayuda/pantallas';
import { EnLinea } from '../ayuda/markdown';

// Botón (?) que va pegado al título de cada pantalla (#129). Muestra una
// ayuda corta de la pantalla actual (ayuda/pantallas.js) y "Ver en el
// manual", que abre /ayuda en otra pestaña para no perder lo que se estaba
// haciendo. Si la ruta no tiene ayuda cargada, no se dibuja nada.
export default function BotonAyuda({ ruta }) {
  const { pathname } = useLocation();
  const ayuda = ayudaDeRuta(ruta || pathname);
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);

  useEffect(() => setAbierto(false), [pathname]);

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    const esc = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', esc);
    };
  }, [abierto]);

  if (!ayuda) return null;

  return (
    <span ref={ref} className="relative ml-2 inline-block align-middle print:hidden">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        title="Ayuda de esta pantalla"
        aria-label="Ayuda de esta pantalla"
        aria-expanded={abierto}
        className={`flex h-7 w-7 items-center justify-center rounded-full transition-colors ${
          abierto ? 'bg-primary-50 text-primary-600' : 'text-slate-400 hover:bg-slate-100 hover:text-primary-600'
        }`}
      >
        <HelpCircle size={18} />
      </button>
      {abierto && (
        <div
          role="dialog"
          aria-label={`Ayuda: ${ayuda.titulo}`}
          className="fixed left-4 right-4 top-20 z-50 space-y-2 rounded-xl border border-slate-200 bg-white p-4 text-left text-sm font-normal leading-relaxed text-slate-600 shadow-lg sm:absolute sm:left-0 sm:right-auto sm:top-full sm:mt-2 sm:w-96"
        >
          <p className="font-semibold text-slate-800">{ayuda.titulo}</p>
          <p><EnLinea texto={ayuda.resumen} /></p>
          {ayuda.puntos?.length > 0 && (
            <ul className="list-disc space-y-1 pl-5">
              {ayuda.puntos.map((p, i) => <li key={i}><EnLinea texto={p} /></li>)}
            </ul>
          )}
          <a
            href={`/ayuda#${ayuda.seccion}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setAbierto(false)}
            className="inline-flex items-center gap-1 pt-1 font-medium text-primary-600 hover:underline"
          >
            Ver en el manual <ExternalLink size={14} />
          </a>
        </div>
      )}
    </span>
  );
}
